import type { ComponentProps } from 'react'
import { cn, tv } from '@joyful/utils'
import type { VariantProps } from '@joyful/utils'

export type DetailsSectionProps = Omit<
  ComponentProps<'section'>,
  'className'
> & {
  className?: string
}

export function DetailsSection({
  children,
  className,
  ...props
}: DetailsSectionProps) {
  return (
    <section className={cn('flex flex-col gap-3', className)} {...props}>
      {children}
    </section>
  )
}

export type DetailsSectionHeaderProps = Omit<
  ComponentProps<'header'>,
  'className'
>

export function DetailsSectionHeader({
  children,
  ...props
}: DetailsSectionHeaderProps) {
  return (
    <header className="flex flex-col gap-0.5" {...props}>
      {children}
    </header>
  )
}

const titleVariants = tv({
  base: null,

  defaultVariants: {
    size: 'md',
    tone: 'strong',
    weight: 'inherit',
  },

  variants: {
    size: {
      lg: 'text-lg font-semibold',
      md: 'text-base font-medium',
      sm: 'text-sm font-medium [&>svg]:size-4 [&>svg]:inline [&>svg]:align-middle [&>svg]:mr-1.5',
    },
    tone: {
      content: 'text-tremor-content',
      inherit: null,
      strong: 'text-tremor-content-strong',
    },
    weight: {
      inherit: null,
      normal: 'font-normal',
    },
  },
})

export type DetailsSectionTitleProps = Omit<ComponentProps<'h2'>, 'className'> &
  VariantProps<typeof titleVariants>

export function DetailsSectionTitle({
  children,
  size,
  tone,
  weight,
  ...props
}: DetailsSectionTitleProps) {
  return (
    <h2 className={titleVariants({ size, tone, weight })} {...props}>
      {children}
    </h2>
  )
}

const subtitleVariants = tv({
  defaultVariants: {
    size: 'sm',
    tone: 'content',
  },

  variants: {
    size: {
      sm: 'text-sm',
      xs: 'text-xs',
    },
    tone: {
      content: 'text-tremor-content',
      inherit: null,
    },
  },
})

export type DetailsSectionSubtitleProps = Omit<
  ComponentProps<'p'>,
  'className'
> &
  VariantProps<typeof subtitleVariants>

export function DetailsSectionSubtitle({
  children,
  size,
  tone,
  ...props
}: DetailsSectionSubtitleProps) {
  return (
    <p className={subtitleVariants({ size, tone })} {...props}>
      {children}
    </p>
  )
}

const detailsSectionContentVariants = tv({
  base: 'flex flex-col',

  compoundVariants: [
    {
      class: 'px-6 py-4',
      padding: 'default',
      size: 'md',
    },
    {
      class: 'px-6 py-3',
      padding: 'default',
      size: 'sm',
    },
  ],

  defaultVariants: {
    gap: 'sm',
    padding: 'default',
    size: 'md',
  },

  variants: {
    gap: {
      lg: 'gap-y-4',
      md: 'gap-y-3',
      none: 'gap-y-0',
      sm: 'gap-y-2',
      xs: 'gap-y-1',
    },
    padding: {
      default: null,
      flush: 'px-0 py-0',
    },
    size: {
      md: null,
      sm: null,
    },
  },
})

export type DetailsSectionContentProps = Omit<
  ComponentProps<'div'>,
  'className'
> &
  VariantProps<typeof detailsSectionContentVariants>

export function DetailsSectionContent({
  gap,
  padding,
  size,
  ...props
}: DetailsSectionContentProps) {
  return (
    <div
      className={detailsSectionContentVariants({
        gap,
        padding,
        size,
      })}
      {...props}
    />
  )
}

const detailsSectionWellVariants = tv({
  base: 'flex flex-col justify-center rounded-md border bg-tremor-background-muted',

  defaultVariants: {
    align: 'center',
    gap: 'none',
    size: 'md',
  },

  slots: {
    text: 'text-tremor-content',
  },

  variants: {
    align: {
      center: {
        base: 'items-center',
      },
      start: {
        base: 'items-start',
      },
      stretch: {
        base: 'items-stretch',
      },
    },
    gap: {
      lg: {
        base: 'gap-4',
      },
      md: {
        base: 'gap-3',
      },
      none: {
        base: null,
      },
      sm: {
        base: 'gap-2',
      },
      xs: {
        base: 'gap-1',
      },
    },
    size: {
      md: {
        base: 'p-4',
        text: 'text-sm',
      },
      sm: {
        base: 'p-3',
        text: 'text-xs',
      },
    },
  },
})

export type DetailsSectionWellProps = Omit<ComponentProps<'div'>, 'className'> &
  VariantProps<typeof detailsSectionWellVariants>

export function DetailsSectionWell({
  align,
  children,
  gap,
  size,
  ...props
}: DetailsSectionWellProps) {
  const classNames = detailsSectionWellVariants({
    align,
    gap,
    size,
  })
  return (
    <div className={classNames.base()} {...props}>
      {typeof children === 'string' ? (
        <p className={classNames.text()}>{children}</p>
      ) : (
        children
      )}
    </div>
  )
}
